import React, { useState, useEffect, useCallback } from 'react'
import { Spin, Input, Pagination } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { COLORS } from 'theme'
import styled from 'styled-components'
import adminRequest from 'utils/adminRequest'
import AntdAvatar from 'components/Avatar'

import {
  SearchSection,
  ShowingText,
  TableTop,
  CustomStyledTable,
} from '../../Style/style'

const InstituteInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`

const InstituteName = styled.span`
  font-weight: 700;
  color: ${COLORS.PRIMARY || '#1B2B65'};
  font-size: 14px;
`

const InstituteCity = styled.span`
  font-size: 13px;
  color: ${COLORS.TEXT_MEDIUM || '#6b7280'};
`

const StyledSearchInput = styled(Input)`
  height: 48px;
  border: none;
  box-shadow: none !important;

  &:focus,
  &:hover,
  &.ant-input-affix-wrapper-focused {
    border: none;
    box-shadow: none !important;
  }

  .ant-input {
    height: 100%;
    border: none;
    box-shadow: none !important;

    &:focus {
      box-shadow: none !important;
    }
  }
`

const ActiveCollegeList = ({ onSelectInstitute }) => {
  const [institutes, setInstitutes] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const pageLimit = 10

  const fetchInstitutes = useCallback(async () => {
    setLoading(true)
    try {
      const params = { pageLimit, pageNo: page }
      if (search) params.searchBy = search
      const response = await adminRequest.get('/assessment/getSubscribedInstitutes', { params })
      const data = response?.data?.data
      setInstitutes((data?.data || []).map(si => ({
        id: si.institute_campus_id || si.institute_id,
        institute_id: si.institute_id,
        name: si.campus_name,
        isActive: true,
        instituteCampus: [{
          id: si.institute_campus_id || si.institute_id,
          campusName: si.campus_name,
          campusLogoUrl: si.campus_logo_url,
          city: si.city,
          state: si.state,
        }],
        subscription_type: si.subscription_type,
        contact_email: si.contact_email,
        contact_number: si.contact_number,
      })))
      setTotalCount(data?.currentCount ?? data?.count ?? 0)
    } catch (error) {
      console.error('Error fetching institutes:', error)
      setInstitutes([])
    } finally {
      setLoading(false)
    }
  }, [page, search])

  useEffect(() => {
    fetchInstitutes()
  }, [fetchInstitutes])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput)
      setPage(0)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput])

  const columns = [
    {
      title: 'INSTITUTE NAME',
      key: 'name',
      render: (_, record) => {
        const campus = record.instituteCampus?.[0]
        const name = campus?.campusName || record.name || '-'
        const city = campus?.city || ''
        const logoUrl = campus?.campusLogoUrl || campus?.campus_logo_url || record.logo || record.logoUrl || ''
        return (
          <InstituteInfo onClick={() => onSelectInstitute(record)}>
            <AntdAvatar
              src={logoUrl}
              size={37}
              IconName={name?.charAt(0)}
              font={18}
            />
            <div>
              <InstituteName>{name}</InstituteName>
              {city && <div><InstituteCity>{city}</InstituteCity></div>}
            </div>
          </InstituteInfo>
        )
      },
    },
    {
      title: 'CITY',
      key: 'city',
      width: 180,
      render: (_, record) => (
        <span style={{ fontWeight: 500, color: '#000' }}>
          {record.instituteCampus?.[0]?.city || '-'}
        </span>
      ),
    },
    {
      title: 'STATE',
      key: 'state',
      width: 180,
      render: (_, record) => (
        <span style={{ fontWeight: 500, color: '#000' }}>
          {record.instituteCampus?.[0]?.state || '-'}
        </span>
      ),
    },
    {
      title: 'STATUS',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <span style={{
          color: record.isActive ? '#52c41a' : '#f5222d',
          fontWeight: 500,
        }}>
          {record.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <TableTop>
        <SearchSection>
          <StyledSearchInput
            placeholder="Search by Institute Name"
            prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </SearchSection>
        <ShowingText>
          Showing {institutes.length} of {totalCount}
        </ShowingText>
      </TableTop>

      <Spin spinning={loading}>
        <CustomStyledTable
          columns={columns}
          dataSource={institutes}
          rowKey="id"
          pagination={false}
          scroll={{ x: '100%' }}
          bordered
          locale={{
            emptyText: <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No institutes found</div>,
          }}
        />
      </Spin>

      {totalCount > pageLimit && (
        <div style={{ margin: '16px', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
          <Pagination
            current={page + 1}
            total={totalCount}
            pageSize={pageLimit}
            onChange={(p) => setPage(p - 1)}
            showSizeChanger={false}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} institutes`}
          />
        </div>
      )}
    </div>
  )
}

export default ActiveCollegeList
